import { useState, useEffect, useRef, useCallback } from 'react';
import { Activity, AlertCircle, ChevronLeft } from 'lucide-react';
import { Drawer } from '@anyemp/ui-kit';
import { Loader } from './LoadingSpinner';
import EntityGroupCard from './EntityGroupCard';
import EntityEventDetail from './EntityEventDetail';
import { users as usersApi, events as eventsApi } from '../../services/apiService';
import { useAuth } from '../../hooks/useAuth';

const TABS = [
  { id: 'all', label: 'All' },
  { id: 'my', label: 'My' },
];

const ActivityFitPanel = ({ isOpen, onClose, entityType, title, extraParams = {} }) => {
  const { hasPermission, user } = useAuth();
  const currentUserId = user?.id;
  const canCancel = hasPermission('talent-service', 'events', 'cancel');

  const [activeTab, setActiveTab] = useState('all');
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(null);
  const [usersMap, setUsersMap] = useState({});

  const pageRef = useRef(1);
  const isLoadingRef = useRef(false);
  const scrollRef = useRef(null);
  const extraParamsRef = useRef(extraParams);
  useEffect(() => { extraParamsRef.current = extraParams; });

  useEffect(() => {
    if (!isOpen) return;
    usersApi.getAllUsers()
      .then(res => {
        const list = res?.success
          ? (Array.isArray(res.data) ? res.data : (res.data?.users || res.data?.data || []))
          : (Array.isArray(res) ? res : []);
        const map = {};
        list.forEach(u => {
          const name = (u.first_name || u.last_name)
            ? `${u.first_name || ''} ${u.last_name || ''}`.trim()
            : (u.name || u.email || String(u.id));
          map[u.id] = { name, avatar: u.avatar || u.photo || null };
        });
        setUsersMap(map);
      })
      .catch(console.error);
  }, [isOpen]);

  const fetchGroups = useCallback(async (page, tab) => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    if (page === 1) setLoading(true);

    try {
      const params = {
        ...extraParamsRef.current,
        page,
        limit: 20,
        'eventable_types[]': entityType,
      };
      if (tab === 'my' && currentUserId) params['user_ids[]'] = currentUserId;

      const res = await eventsApi.getGroupedEvents(params);
      const newGroups = res.data || [];
      setGroups(prev => page === 1 ? newGroups : [...prev, ...newGroups]);
      setTotal(res.pagination.total);
      setHasMore(page < res.pagination.totalPages);
      pageRef.current = page;
      setError(null);
    } catch {
      setError('Failed to load activity');
    } finally {
      isLoadingRef.current = false;
      setLoading(false);
    }
  }, [entityType, currentUserId]);

  // Stable key from extraParams to trigger re-fetch when context changes
  const extraParamsKey = JSON.stringify(extraParams);

  useEffect(() => {
    if (!isOpen) return;
    pageRef.current = 1;
    setGroups([]);
    setSelectedGroup(null);
    setHasMore(true);
    fetchGroups(1, activeTab);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeTab, fetchGroups, extraParamsKey]);

  useEffect(() => {
    const panel = scrollRef.current;
    if (!panel || !isOpen || selectedGroup) return;
    const onScroll = () => {
      if (!hasMore || isLoadingRef.current) return;
      if (panel.scrollHeight - panel.scrollTop - panel.clientHeight < 300) {
        fetchGroups(pageRef.current + 1, activeTab);
      }
    };
    panel.addEventListener('scroll', onScroll, { passive: true });
    return () => panel.removeEventListener('scroll', onScroll);
  }, [isOpen, hasMore, fetchGroups, activeTab, selectedGroup]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSelectedGroup(null);
  };

  const handleSelectGroup = (group) => {
    setSelectedGroup(prev =>
      prev?.entityType === group.entityType && prev?.entityId === group.entityId
        ? null : group
    );
  };

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      anchor="right"
      width="min(480px, 100vw)"
      showCloseButton
      style={{ padding: 0, paddingTop: 'max(0.75rem, env(safe-area-inset-top))', backgroundColor: 'var(--palette-background-default)' }}
    >
      <div className="h-full flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-light-border-primary dark:border-dark-border-primary shrink-0 pr-10">
          <div className="flex items-center gap-2 min-w-0">
            {selectedGroup ? (
              <button
                onClick={() => setSelectedGroup(null)}
                className="text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary p-0.5 rounded transition-colors shrink-0"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
            ) : (
              <Activity className="h-5 w-5 text-primary-600 dark:text-primary-400 shrink-0" />
            )}
            <h2 className="text-base font-semibold text-light-text-primary dark:text-dark-text-primary truncate">
              {selectedGroup ? selectedGroup.entityName : title}
            </h2>
            {!selectedGroup && total > 0 && (
              <span className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary shrink-0">
                ({total.toLocaleString()})
              </span>
            )}
          </div>
        </div>

        {/* Tabs */}
        {!selectedGroup && (
          <div className="flex border-b border-light-border-primary dark:border-dark-border-primary shrink-0">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary-600 text-primary-700 dark:text-primary-400 dark:border-primary-400'
                    : 'border-transparent text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {selectedGroup ? (
            <div className="h-full overflow-hidden p-4">
              <EntityEventDetail
                key={`${selectedGroup.entityType}_${selectedGroup.entityId}`}
                group={selectedGroup}
                usersMap={usersMap}
                canCancel={canCancel}
                onClose={() => setSelectedGroup(null)}
              />
            </div>
          ) : (
            <div ref={scrollRef} className="h-full overflow-y-auto p-4">
              {loading && groups.length === 0 ? (
                <Loader variant="component" size="md" fullHeight={false} />
              ) : error ? (
                <div className="text-center py-10">
                  <AlertCircle className="h-10 w-10 text-red-400 mx-auto mb-3" />
                  <p className="text-red-600 dark:text-red-400 mb-3">{error}</p>
                  <button
                    onClick={() => fetchGroups(1, activeTab)}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md text-sm hover:bg-primary-700 transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              ) : groups.length === 0 ? (
                <div className="text-center py-10 text-light-text-tertiary dark:text-dark-text-tertiary">
                  <Activity className="h-10 w-10 mx-auto mb-3 opacity-40" />
                  <p>No activity found</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {groups.map(group => {
                    const key = `${group.entityType}_${group.entityId}`;
                    const isSelected = selectedGroup?.entityType === group.entityType && selectedGroup?.entityId === group.entityId;
                    return (
                      <EntityGroupCard
                        key={key}
                        group={group}
                        usersMap={usersMap}
                        isSelected={isSelected}
                        onSelect={handleSelectGroup}
                      />
                    );
                  })}
                  {loading && (
                    <div className="flex justify-center py-3">
                      <Loader variant="component" size="sm" fullHeight={false} />
                    </div>
                  )}
                  {!hasMore && groups.length > 0 && (
                    <p className="text-center text-xs text-light-text-tertiary dark:text-dark-text-tertiary py-3">
                      All {total.toLocaleString()} entities loaded
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default ActivityFitPanel;
